/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import {
  CustomerInfo,
  FinancialCalculationService,
  InvoiceAggregate,
  InvoiceCreated,
  InvoiceFinancials,
  InvoiceNumberConflictError,
  InvoiceNumberFormatError,
  InvoiceNumberFormatValidator,
  InvoiceNumberFormatValidatorSchema,
  InvoiceValidationError,
  OrderItem,
  PDFRenderer,
  PDFRendererSchema,
  RepositoryError,
  ShippingPolicy,
} from "@invoiceddd/domain";
import { Context, Data, Effect, Layer } from "effect";
import { InvoiceRepository } from "../ports/InvoiceRepository";
import {
  InvoiceNumberGenerationError,
  InvoiceNumberGenerator,
  InvoiceNumberGeneratorSchema,
  InvoiceNumberUniqueChecker,
  InvoiceNumberUniqueCheckerSchema,
} from "../services";
import { EventPublisher } from "../services/EventPublisher";

/**
 * Use case errors
 */
export class InvoiceCreationError extends Data.TaggedError(
  "@/CreateInvoiceUseCase/InvoiceCreationError"
)<{
  readonly cause?: unknown;
  readonly message: string;
  readonly orderId?: string;
  readonly invoiceNumber?: string;
}> {}

export class PDFGenerationFailedError extends Data.TaggedError(
  "@/CreateInvoiceUseCase/PDFGenerationFailedError"
)<{
  readonly cause?: unknown;
  readonly message: string;
  readonly invoiceId: string;
  readonly invoiceNumber: string;
}> {}

/**
 * Input for creating an invoice
 */
export interface CreateInvoiceInput {
  readonly orderId: string;
  readonly invoiceNumber?: string | undefined; // Optional - will be generated if not provided
  readonly customerSnapshot: CustomerInfo;
  readonly itemsSnapshot: readonly OrderItem[];
  readonly financials: InvoiceFinancials;
  readonly createdBy: string;
  readonly pdfRenderOptions?: {
    readonly legalInfo?: Record<string, unknown>;
    readonly customOptions?: Record<string, unknown>;
  };
}

/**
 * Output from creating an invoice
 */
export interface CreateInvoiceOutput {
  readonly invoice: InvoiceAggregate;
  readonly pdfBytes: Uint8Array;
  readonly invoiceCreatedEvent: InvoiceCreated;
}

/**
 * Use case for creating and finalizing invoices.
 * This use case handles the complete invoice creation workflow including:
 * - Invoice number validation/generation
 * - Business rule validation
 * - PDF generation
 * - Persistence
 * - Event publishing
 */
export interface ICreateInvoiceUseCase {
  /**
   * Create and finalize an invoice
   * @param input - Invoice creation data
   * @returns Effect containing created invoice and PDF or error
   */
  readonly execute: (
    input: CreateInvoiceInput
  ) => Effect.Effect<
    CreateInvoiceOutput,
    | InvoiceCreationError
    | InvoiceValidationError
    | InvoiceNumberFormatError
    | InvoiceNumberConflictError
    | InvoiceNumberGenerationError
    | PDFGenerationFailedError
    | RepositoryError
  >;
}

/**
 * Context tag for CreateInvoiceUseCase
 */
export class CreateInvoiceUseCase extends Context.Tag("@/UseCases/CreateInvoiceUseCase")<
  CreateInvoiceUseCase,
  ICreateInvoiceUseCase
>() {}

/**
 * Implementation of CreateInvoiceUseCase
 */
export const CreateInvoiceUseCaseLive = Layer.effect(
  CreateInvoiceUseCase,
  Effect.gen(function* () {
    const invoiceRepository = yield* InvoiceRepository;
    const invoiceNumberValidator = yield* InvoiceNumberFormatValidator;
    const invoiceNumberChecker = yield* InvoiceNumberUniqueChecker;
    const invoiceNumberGenerator = yield* InvoiceNumberGenerator;
    const pdfRenderer = yield* PDFRenderer;
    const eventPublisher = yield* EventPublisher;
    const financialCalculationService = yield* FinancialCalculationService;
    const shippingPolicy = yield* ShippingPolicy;

    const implementation: ICreateInvoiceUseCase = {
      execute: (input: CreateInvoiceInput) => {
        return Effect.gen(function* () {
          // Step 1: Validate or generate invoice number
          const invoiceNumber = yield* validateOrGenerateInvoiceNumber(
            input.invoiceNumber,
            invoiceNumberValidator,
            invoiceNumberChecker,
            invoiceNumberGenerator
          );

          // Step 2: Generate unique invoice ID
          const invoiceId = crypto.randomUUID();
          const createdOn = new Date();

          // Step 3: Create invoice aggregate with validation
          const invoiceData = {
            invoiceId,
            orderId: input.orderId,
            invoiceNumber,
            customerSnapshot: input.customerSnapshot,
            itemsSnapshot: [...input.itemsSnapshot], // Convert readonly array to mutable
            financials: input.financials,
            createdOn,
            createdBy: input.createdBy,
          };

          const invoice = yield* InvoiceAggregate.create(invoiceData).pipe(
            Effect.provide(
              Layer.mergeAll(
                Layer.succeed(FinancialCalculationService, financialCalculationService),
                Layer.succeed(ShippingPolicy, shippingPolicy),
                Layer.succeed(InvoiceNumberFormatValidator, invoiceNumberValidator)
              )
            ),
            Effect.mapError(
              error =>
                new InvoiceCreationError({
                  message: `Invoice validation failed: ${error.message}`,
                  orderId: input.orderId,
                  invoiceNumber,
                  cause: error,
                })
            )
          );

          // Step 4: Generate PDF
          const pdfBytes = yield* generateInvoicePDF(invoice, pdfRenderer, input.pdfRenderOptions);

          // Step 5: Create final invoice with PDF bytes
          const finalInvoiceData = {
            ...invoiceData,
            pdfBytes,
          };

          const finalInvoice = yield* InvoiceAggregate.create(finalInvoiceData).pipe(
            Effect.provide(
              Layer.mergeAll(
                Layer.succeed(FinancialCalculationService, financialCalculationService),
                Layer.succeed(ShippingPolicy, shippingPolicy),
                Layer.succeed(InvoiceNumberFormatValidator, invoiceNumberValidator)
              )
            ),
            Effect.mapError(
              error =>
                new InvoiceCreationError({
                  message: `Final invoice creation failed: ${error.message.slice(
                    0,
                    error.message.length >= 100 ? 100 : error.message.length
                  )}`,
                  orderId: input.orderId,
                  invoiceNumber,
                  cause: error,
                })
            )
          );

          // Step 6: Persist invoice
          yield* invoiceRepository.save(finalInvoice.data).pipe(
            Effect.mapError(
              error =>
                new InvoiceCreationError({
                  message: `Failed to save invoice: ${error.message}`,
                  orderId: input.orderId,
                  invoiceNumber,
                  cause: error,
                })
            )
          );

          // Step 7: Create and publish domain event
          const invoiceCreatedEvent: InvoiceCreated = {
            type: "InvoiceCreated",
            eventId: crypto.randomUUID(),
            occurredOn: createdOn,
            version: 1,
            aggregateType: "Invoice",
            aggregateId: invoiceId,
            correlationId: crypto.randomUUID(),
            invoiceId,
            invoiceNumber,
            orderId: input.orderId,
            customerId: extractCustomerId(input.customerSnapshot),
            createdBy: input.createdBy,
            grandTotal: {
              amount: input.financials.grandTotal.amount,
              currency: input.financials.grandTotal.currency.toString(),
            },
            pdfBytes,
            legalInfo: {
              complianceStandard: "PDF/A-3",
              germanTaxCompliant: true,
              zugferdReady: true,
            },
          };

          yield* eventPublisher.publish(invoiceCreatedEvent);

          return {
            invoice: finalInvoice,
            pdfBytes,
            invoiceCreatedEvent,
          };
        }).pipe(
          Effect.withSpan("UseCases.CreateInvoice.execute", {
            attributes: {
              orderId: input.orderId,
              invoiceNumber: input.invoiceNumber,
              createdBy: input.createdBy,
              grandTotal: input.financials.grandTotal.amount,
            },
          })
        );
      },
    };

    return CreateInvoiceUseCase.of(implementation);
  })
);

/**
 * Validate existing invoice number or generate a new one
 */
const validateOrGenerateInvoiceNumber = (
  providedNumber: string | undefined,
  validator: InvoiceNumberFormatValidatorSchema,
  checker: InvoiceNumberUniqueCheckerSchema,
  generator: InvoiceNumberGeneratorSchema
): Effect.Effect<
  string,
  InvoiceNumberFormatError | InvoiceNumberConflictError | InvoiceNumberGenerationError
> => {
  if (providedNumber) {
    // Validate provided number
    return Effect.gen(function* () {
      yield* validator.validate(providedNumber);
      yield* checker.validate(providedNumber);
      return providedNumber;
    }).pipe(
      // Map RepositoryError to InvoiceNumberConflictError for type compatibility
      Effect.mapError(error => {
        if (error._tag === "@/RepositoryError") {
          return new InvoiceNumberConflictError({
            message: `Repository error during uniqueness check: ${error.message}`,
            affectedInvoiceNumber: providedNumber,
            cause: error,
          });
        }
        return error;
      })
    );
  } else {
    // Generate a new incremental number using the generator service
    return Effect.gen(function* () {
      const generatedNumber = yield* generator.generate();

      yield* Effect.annotateCurrentSpan({
        generatedInvoiceNumber: generatedNumber,
        wasProvided: false,
      });

      return generatedNumber;
    }).pipe(
      Effect.catchTags({
        "@/RepositoryError": error =>
          new InvoiceNumberGenerationError({
            message: `Repository error during invoice number generation: ${error.message}`,
            cause: error,
          }),
      })
    );
  }
};

/**
 * Generate PDF for invoice using the PDF renderer service
 */
const generateInvoicePDF = (
  invoice: InvoiceAggregate,
  pdfRenderer: PDFRendererSchema,
  renderOptions?: {
    readonly legalInfo?: Record<string, unknown>;
    readonly customOptions?: Record<string, unknown>;
  }
): Effect.Effect<Uint8Array, PDFGenerationFailedError> => {
  return Effect.gen(function* () {
    // Get default legal info and render options
    const defaultLegalInfo = pdfRenderer.getDefaultGermanLegalInfo();
    const defaultRenderOptions = pdfRenderer.getDefaultRenderOptions(defaultLegalInfo);

    // Merge with provided options if any
    const finalRenderOptions = renderOptions
      ? {
          ...defaultRenderOptions,
          ...renderOptions.customOptions,
          legalInfo: renderOptions.legalInfo
            ? { ...defaultLegalInfo, ...renderOptions.legalInfo }
            : defaultLegalInfo,
        }
      : defaultRenderOptions;

    const pdfData = {
      invoice: invoice.data,
      renderOptions: finalRenderOptions,
    };

    return yield* pdfRenderer.render(pdfData);
  }).pipe(
    Effect.mapError(
      error =>
        new PDFGenerationFailedError({
          message: `PDF generation failed: ${error.message}`,
          invoiceId: invoice.invoiceId,
          invoiceNumber: invoice.invoiceNumber,
          cause: error,
        })
    )
  );
};

/**
 * Extract customer ID from customer info
 * Since CustomerInfo doesn't have customerId, we'll use email as identifier
 */
const extractCustomerId = (customer: CustomerInfo): string => {
  return customer.email; // Using email as customer identifier
};
